import React, { useEffect, useMemo } from 'react'

import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticProps } from 'next'
import { ArticleJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'

import { getLayout } from '@/components/layouts/opportunity/Layout'
import { useBreadcrumb } from '@/components/opportunity/common/BreadcrumbV2/context'
import {
  OpportunityDetailShell,
  OpportunityPageLayout,
} from '@/components/opportunity/common/OpportunityDetailShell'
import { normalizeDepartmentName } from '@/components/opportunity/common/transforms'
import { GrantDisplayV2 } from '@/components/opportunity/FederalGrant/DisplayV2'
import { GrantDisplayV2Skeleton } from '@/components/opportunity/FederalGrant/DisplayV2/Skeleton'

import { FRONTEND_URL } from '@/constants/env'

import { UUID_REGEX } from '@/helpers/id'
import { capitalizeString } from '@/helpers/string'

import { useShare } from '@/hooks/common/useShare'
import {
  federalGrantWithoutPresignedUrlsQueryKey,
  fetchFederalGrantWithoutPresignedUrls,
  useFederalGrantPresignedUrls,
  useSuspenseFederalGrantWithoutPresignedUrls,
} from '@/hooks/federal-grant/useFederalGrant'
import { useIndexOpportunity } from '@/hooks/opportunity/useIndexOpportunity'
import { useSimilarOpportunities } from '@/hooks/similar-opportunities'

import type { FederalGrantNotice } from '@/types/federal-grant/FederalGrant'
import type { NextPageWithLayout, WithRenderedAt } from '@/types/next'

type FederalGrantSolicitationPageProps = WithRenderedAt & {
  id: string
  seoTitle: string
  dehydratedState: ReturnType<typeof dehydrate>
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = (async (context) => {
  const id = context.params?.id
  const notFoundResponse = {
    redirect: {
      destination: '/404',
      permanent: false,
    },
  }

  if (id === undefined || typeof id !== 'string') return notFoundResponse

  // Non-UUID IDs are USASpending IDs — redirect to award page
  if (!UUID_REGEX.test(id)) {
    return {
      redirect: {
        destination: `/federal-grants/${id}/award`,
        permanent: false,
      },
    }
  }

  try {
    const federalGrant = await fetchFederalGrantWithoutPresignedUrls(id)

    if (!federalGrant || federalGrant.notices.length === 0)
      return notFoundResponse

    const queryClient = new QueryClient()
    queryClient.setQueryData(
      federalGrantWithoutPresignedUrlsQueryKey(id),
      federalGrant,
    )

    const latestNotice = [...federalGrant.notices].sort(
      (a, b) =>
        new Date(b.post_date).getTime() - new Date(a.post_date).getTime(),
    )[0]

    return {
      props: {
        id,
        seoTitle:
          latestNotice?.opportunity_title ?? 'Federal Grant Opportunity',
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        __renderedAt: new Date().toISOString(),
      },
      revalidate: 7200, // revalidate every 2 hours
    }
  } catch (e) {
    if (e instanceof Response && e.status === 404) {
      return notFoundResponse
    }
    if (!(e instanceof Error)) {
      const errorMessage = JSON.stringify(e)
      throw new Error(
        `error occurred while fetching federal grant: ${errorMessage}`,
      )
    }
    throw e
  }
}) satisfies GetStaticProps<FederalGrantSolicitationPageProps>

/**
 * Federal Grant Solicitation Page (Public, ISR)
 *
 * Displays solicitation/opportunity details for a federal grant using the V2 design.
 * Data is pre-fetched via getStaticProps, dehydrated into the React Query cache,
 * and revalidated every 2 hours. Similar opportunities load progressively client-side.
 *
 * Path: /federal-grants/[id]
 */
const FederalGrantSolicitationPage: NextPageWithLayout = ({
  id,
  seoTitle,
  dehydratedState,
}: FederalGrantSolicitationPageProps) => {
  return (
    <OpportunityDetailShell
      seoTitle={seoTitle}
      dehydratedState={dehydratedState}
      errorTitle="Failed to load federal grant"
      notFoundTitle="Grant Not Found"
      backHref="/federal-grants/search"
      backLabel="Browse Federal Grants"
      fallback={
        <OpportunityPageLayout>
          <GrantDisplayV2Skeleton />
        </OpportunityPageLayout>
      }
    >
      <FederalGrantSolicitationContent id={id} />
    </OpportunityDetailShell>
  )
}

function FederalGrantSolicitationContent({ id }: { id: string }) {
  useIndexOpportunity(id, 'federal_grant')

  // The ISR copy can be hours old, so prefer the live record, as long as it has a notice to display.
  const { data: baseFederalGrant } =
    useSuspenseFederalGrantWithoutPresignedUrls(id)
  const { data: presignedFederalGrant } = useFederalGrantPresignedUrls(id)
  const federalGrant = presignedFederalGrant?.notices.length
    ? presignedFederalGrant
    : baseFederalGrant

  // Fetch similar opportunities (loads client-side)
  const { data: similarOpportunities, isLoading: isSimilarLoading } =
    useSimilarOpportunities('federal_grant', id)

  const { setItems, clearItems } = useBreadcrumb()

  const latestNotice = useMemo<FederalGrantNotice | null>(
    () =>
      federalGrant.notices.length > 0
        ? [...federalGrant.notices].sort(
            (a, b) =>
              new Date(b.post_date).getTime() - new Date(a.post_date).getTime(),
          )[0]
        : null,
    [federalGrant.notices],
  )

  const earliestNotice = useMemo<FederalGrantNotice | null>(
    () =>
      federalGrant.notices.length > 0
        ? [...federalGrant.notices].sort(
            (a, b) =>
              new Date(a.post_date).getTime() - new Date(b.post_date).getTime(),
          )[0]
        : null,
    [federalGrant.notices],
  )

  useEffect(() => {
    if (federalGrant && latestNotice) {
      const agencyRaw = latestNotice.agency_name || 'Federal Agency'
      const agencyDisplay = normalizeDepartmentName(agencyRaw) ?? agencyRaw
      setItems([
        { name: 'Federal Grants', href: '/federal-grants/search' },
        {
          name: agencyDisplay,
          href: `/federal-grants/search?agencies=${encodeURIComponent(agencyRaw)}`,
        },
        {
          name:
            latestNotice.opportunity_number ?? latestNotice.opportunity_title,
        },
      ])
    }
    return () => clearItems()
  }, [federalGrant, latestNotice, setItems, clearItems])

  const handleShare = useShare()

  const title = latestNotice?.opportunity_title ?? 'Federal Grant Opportunity'

  const seoDescription = useMemo(() => {
    if (!federalGrant) return ''
    return federalGrant.ai_summary.split('. ').slice(0, 3).join('. ') + '.'
  }, [federalGrant])

  const ogImageUrl = useMemo(() => {
    if (!latestNotice) return ''
    let url = `${FRONTEND_URL}/api/og?title=${encodeURIComponent(
      latestNotice.opportunity_title,
    )}`
    if (latestNotice.agency_name) {
      url += `&description=${encodeURIComponent(
        capitalizeString(latestNotice.agency_name),
      )}`
    }
    url += `&subdescription=${encodeURIComponent('Federal Grant Opportunity')}`
    return url
  }, [latestNotice])

  return (
    <>
      <NextSeo
        title={title}
        description={seoDescription}
        additionalLinkTags={[
          {
            rel: 'icon',
            href: `${FRONTEND_URL}/favicon.ico`,
          },
        ]}
        openGraph={{
          description: seoDescription,
          images: [
            {
              url: ogImageUrl,
              width: 1200,
              height: 632,
              alt: title,
            },
          ],
          siteName: 'Sweetspot',
          title: title,
          url: `${FRONTEND_URL}/federal-grants/${federalGrant.id}`,
        }}
        twitter={{
          handle: '@SweetspotGov',
          site: '@SweetspotGov',
          cardType: 'summary_large_image',
        }}
      />
      <ArticleJsonLd
        url={`${FRONTEND_URL}/federal-grants/${federalGrant.id}`}
        title={title}
        images={[ogImageUrl]}
        datePublished={earliestNotice?.post_date ?? ''}
        dateModified={latestNotice?.post_date ?? ''}
        authorName={[
          {
            name: 'Sweetspot',
            url: FRONTEND_URL,
          },
        ]}
        publisherName="Sweetspot"
        publisherLogo={`${FRONTEND_URL}/logo.png`}
        description={federalGrant.ai_summary}
        isAccessibleForFree={true}
      />
      {/*
        for some reason, Twitter cards using standard OG tags don't work, need to add this manually;
        https://github.com/garmeeh/next-seo/issues/14;
      */}
      <Head>
        <meta name="robots" content="noarchive" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:image" content={ogImageUrl} />
        <meta property="application-name" content="Sweetspot" />
      </Head>
      <OpportunityPageLayout>
        <GrantDisplayV2
          grant={federalGrant}
          similarOpportunities={similarOpportunities}
          isSimilarLoading={isSimilarLoading}
          onShare={handleShare}
        />
      </OpportunityPageLayout>
    </>
  )
}

FederalGrantSolicitationPage.getLayout = getLayout
FederalGrantSolicitationPage.requiresAuth = false

export default FederalGrantSolicitationPage
